import { I18n } from 'react-redux-i18n';
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';

import { Button } from '@common/components/Button';
import { MessageBox } from '@common/components/MessageBox';
import { getTestAttributes } from '@common/utils/tests';
import { Flex } from '@common/components/Flex';
import { ImportFileInput } from './components/file-input';
import { ImportConfiguration } from './components/configuration';
import { IMPORT_TYPE, FILE_DELIMITER, INDIVIDUALS_BLANK_SETTING, START_AT_ROW } from './pedigree-import.constants';
import { FormatManagement, FORMAT_MANAGEMENT_MODE } from './components/format-management';
import { autoBuild, createFolder, createFormat, importIndividual, importPedigree, uploadFile } from './actions/main';
import { FileView } from './components/file-view';
import { ErrorMessage } from '@common/components/error-message';
import { InfoMessage } from '@common/components/info-message';

import './pedigree-import.styles.scss';

export const MODULE_NAME = 'PEDIGREE_IMPORT';

export class PedigreeImport extends React.Component {
  state = {
    submitting: false,
    modalHidden: false,
    messageProps: {
      header: '',
      text: '',
      show: false,
      confirmText: 'Ok',
      cancelText: '',
      size: 'small',
      onCustomOk: () => {},
    },
    formatManagement: {
      opened: false,
      mode: undefined
    },
    columnsTypeIndex: {},
    columnsFieldIndex: {},
    parsedFile: undefined,
    importType: IMPORT_TYPE.pedigrees,
    fileDelimiter: FILE_DELIMITER.txtTab,
    customFileDelimiter: '',
    hasHeader: false,
    maleValue: 'M',
    femaleValue: 'F',
    deceasedValue: '',
    shouldUpdateExistingIndividual: false,
    individualsBlankSetting: INDIVIDUALS_BLANK_SETTING.dontImportBlanks,
  };

  handleImportTypeChange = (importType) => {
    this.setState((x) => ({ ...x, importType }));
  }

  handleFileDelimiterChange = (fileDelimiter) => {
    this.setState((x) => ({ ...x, fileDelimiter }));
  }

  handleCustomFileDelimiterChange = (customFileDelimiter) => {
    this.setState((x) => ({ ...x, customFileDelimiter }));
  }

  handleHasHeaderChange = (hasHeader) => {
    this.setState((x) => ({ ...x, hasHeader }));
  }

  handleImportOptionsChange = (importOption) => {
    this.setState((x) => ({ ...x, ...importOption }));
  }

  handleShouldExistingIndividualChange = (shouldUpdateExistingIndividual) => {
    this.setState((x) => ({ ...x, shouldUpdateExistingIndividual }));
  }

  handleIndividualsBlankSettingChange = (individualsBlankSetting) => {
    this.setState((x) => ({ ...x, individualsBlankSetting }));
  }

  handleImport = async () => {
    const { close } = this.props;

    try {
      this.setState((state) => ({ ...state, submitting: true })); 

      const individualData = {
        updateExisting: this.state.shouldUpdateExistingIndividual,
        overwriteEmpty: this.state.individualsBlankSetting === INDIVIDUALS_BLANK_SETTING.importBlanks,
      };
      const data = {
        folderId: this.props.folderId,
        maleValue: this.state.maleValue,
        femaleValue: this.state.femaleValue,
        deceasedValue: this.state.deceasedValue,
        columns: Object.keys(this.state.columnsFieldIndex).reduce((acc, index) => {
          const action = this.state.columnsFieldIndex[index]?.action;
          if (action) {
            acc[action] = Number(index);
          }
          return acc;
        }, {}),
        existingFieldsColumns: Object.keys(this.state.columnsFieldIndex).reduce((acc, index) => {
          const fieldId = this.state.columnsFieldIndex[index]?.id;
          if (fieldId != null) {
            acc[fieldId] = Number(index);
          }
          return acc;
        }, {}),
        headerRow: this.state.hasHeader,
        ...individualData,
      };

      const request = {
        [IMPORT_TYPE.individuals]: importIndividual,
        [IMPORT_TYPE.pedigrees]: importPedigree,
      }[this.state.importType];

      const response = await request(data);
      const messages = Object.keys(response).map((key) => `${key} - ${response[key]}`);

      this.setState((x) => ({ ...x, modalHidden: true }));

      this.setMessageProps({
        header: I18n.t('Information Message'),
        content: <InfoMessage>{messages.map((x) => <div key={x}>{x}</div>)}</InfoMessage>,
        show: true,
        confirmText: 'Ok',
        onCustomOk: close,
      });
    } catch (err) {
      this.showErrorMessage(err?.entity || I18n.t('Failed to import'));
      console.error(err);
    } finally {
      this.setState((state) => ({ ...state, submitting: false })); 
    }
  }

  setMessageProps = (props) => {
    this.setState((state) => ({
      ...state,
      messageProps: {
        size: 'small',
        ...props,
      },
    }));
  }

  closeMessage = () => {
    this.setMessageProps({ show: false });
  }

  handleLoadFormatClick = () => {
    this.setState((x) => ({
      ...x,
      formatManagement: {
        ...x.formatManagement,
        opened: true,
        mode: FORMAT_MANAGEMENT_MODE.load,
      },
    }));
  }

  handleSaveFormatClick = () => {
    this.setState((x) => ({
      ...x,
      formatManagement: {
        ...x.formatManagement,
        opened: true,
        mode: FORMAT_MANAGEMENT_MODE.save,
      },
    }));
  }

  closeFormatManagement = () => {
    this.setState((x) => ({
      ...x,
      formatManagement: {
        ...x.formatManagement,
        opened: false,
        mode: undefined,
      },
    }));
  }

  showErrorMessage = (message = I18n.t('Operation failed')) => {
    this.setMessageProps({
      header: I18n.t('Operation canceled'),
      content: <ErrorMessage>{message}</ErrorMessage>,
      show: true,
      confirmText: 'Ok',
      onCustomOk: this.closeMessage,
    });
}

  handleFormatManagementSubmit = async (data) => {
    const { formatManagement } = this.state;

    if (formatManagement.mode === FORMAT_MANAGEMENT_MODE.load) {
      const predefinedDelimiters = [FILE_DELIMITER.txtComma, FILE_DELIMITER.txtTab];
      const isCustomDelimiter = !predefinedDelimiters.includes(data.delimiter);

      this.setState((x) => ({
        ...x,
        importType: data.importType,
        fileDelimiter: isCustomDelimiter ? FILE_DELIMITER.txtOther : data.delimiter,
        customFileDelimiter: isCustomDelimiter ? data.delimiter : '',
        hasHeader: data.startAtRow === START_AT_ROW.yes,
        maleValue: data.maleValue,
        femaleValue: data.femaleValue,
        deceasedValue: data.deceasedValue,
        individualsBlankSetting: data.importType === IMPORT_TYPE.individuals ? data.importBlanks : INDIVIDUALS_BLANK_SETTING.dontImportBlanks,
      }));

      this.closeFormatManagement();
    }

    if (formatManagement.mode === FORMAT_MANAGEMENT_MODE.save) {
      const { folderId, name } = data;
      const { importType, customFileDelimiter, fileDelimiter, maleValue, femaleValue, deceasedValue, hasHeader, individualsBlankSetting } = this.state;

      try {
        await createFormat({
          folderId,
          formatName: name,
          importType,
          delimiter: customFileDelimiter || fileDelimiter,
          maleValue,
          femaleValue,
          deceasedValue,
          startAtRow: hasHeader ? START_AT_ROW.yes : START_AT_ROW.no,
          importBlanks: importType === IMPORT_TYPE.individuals ? individualsBlankSetting : INDIVIDUALS_BLANK_SETTING.dontImportBlanks,
          namingOption: 0,
          defaultPrefix: "",
          defaultSuffix: "",
          defaultStartAt: 0,
        }).then(this.closeFormatManagement);
      } catch (err) {
        console.error(err);
        this.showErrorMessage(err?.entity || I18n.t('Failed to create a format'));
      }
    }
  }

  handleUpload = async (file) => {
    try {
      const parsedFile = await uploadFile(file, this.state.customFileDelimiter || this.state.fileDelimiter);

      this.setState((x) => ({ ...x, parsedFile }));
    } catch (err) {
      console.error(err);
      this.showErrorMessage(err?.entity || I18n.t('Failed to upload file'));
    }
  }

  handleColumnsTypeIndexChange = (columnsTypeIndex) => {
    this.setState((x) => ({ ...x, columnsTypeIndex }));
  }

  handleColumnsFieldIndexChange = (columnsFieldIndex) => {
    this.setState((x) => ({ ...x, columnsFieldIndex }));
  }

  handleAutoBuild = async () => {
    try {
      const builtFile = await autoBuild();

      this.setState((prev) => ({
        ...prev,
        columnsFieldIndex: builtFile.columns.reduce((acc, col) => {
          acc[col.column] = {
            action: col.action,
            name: col.fieldName,
            imageURL: col.fieldImageUrl,
            id: col.fieldId
          };
          return acc;
        }, {})
      }));

      if (!builtFile.allFieldsMapped) {
        this.setMessageProps({
          header: I18n.t('Information Message'),
          content: <InfoMessage>{I18n.t('There are names in your header row that we could not match because of no match or multiple matches found to fields in the database')}</InfoMessage>,
          show: true,
          confirmText: 'Ok',
          onCustomOk: this.closeMessage,
        });
      }
    } catch (err) {
      console.error(err);
      this.showErrorMessage(err?.entity || I18n.t('Failed to auto build'));
    }
  }

  canUploadFile = () => {
    const config = [
      { condition: this.state.fileDelimiter === FILE_DELIMITER.txtOther && !this.state.customFileDelimiter, message: I18n.t('Please, specify file delimiter') },
    ];
    const message = config.find((x) => x.condition)?.message;

    if (message) {
      this.showErrorMessage(message);

      return false;
    }

    return true;
  }

  handleFolderCreate = async(...args) => {
    try {
      await createFolder(...args);
    } catch (err) {
      this.showErrorMessage(err?.entity || I18n.t('Failed to create a folder'));
      throw err;
    }
  }

  render() {
    const { 
      submitting, 
      columnsTypeIndex, 
      parsedFile, 
      importType, 
      fileDelimiter, 
      customFileDelimiter, 
      hasHeader, 
      formatManagement, 
      femaleValue, 
      maleValue, 
      deceasedValue, 
      shouldUpdateExistingIndividual, 
      individualsBlankSetting,
      columnsFieldIndex,
      modalHidden,
    } = this.state;
    const { close } = this.props;

    return (
      <Modal
        show
        animation={false}
        className="PedigreeImport"
        backdrop="static"
        bsClass="bootstrap modal"
        dialogClassName="PedigreeImport"
        onEscapeKeyDown={close}
        onHide={close}
        style={{ opacity: modalHidden ? 0 : 1 }}
      >
        <MessageBox
          hideMessageBox={this.closeMessage}
          {...this.state.messageProps}
        />
        <ModalHeader closeButton onHide={close}>
          <ModalTitle {...getTestAttributes(MODULE_NAME, 'modal', 'title')}>{I18n.t('Import')}</ModalTitle>
        </ModalHeader>
        <ModalBody className="PedigreeImport__content">
          <ImportFileInput 
            canUploadFile={this.canUploadFile}
            onUpload={this.handleUpload}
          />
          <ImportConfiguration 
            importType={importType}
            fileDelimiter={fileDelimiter}
            customFileDelimiter={customFileDelimiter}
            hasHeader={hasHeader}
            femaleValue={femaleValue}
            maleValue={maleValue}
            deceasedValue={deceasedValue}
            individualsBlankSetting={individualsBlankSetting}
            shouldUpdateExistingIndividual={shouldUpdateExistingIndividual}
            hasHeaderDisabled={!parsedFile}
            autoBuildDisabled={!parsedFile || !hasHeader}
            onImportTypeChange={this.handleImportTypeChange}
            onFileDelimiterChange={this.handleFileDelimiterChange}
            onCustomFileDelimiterChange={this.handleCustomFileDelimiterChange}
            onImportOptionChange={this.handleImportOptionsChange}
            onHasHeaderChange={this.handleHasHeaderChange}
            onShouldExistingIndividualChange={this.handleShouldExistingIndividualChange}
            onIndividualsBlankSettingChange={this.handleIndividualsBlankSettingChange}
            onAutoBuild={this.handleAutoBuild}
          />
          <FileView 
            {...parsedFile}
            importType={importType}
            columnsTypeIndex={columnsTypeIndex}
            columnsFieldIndex={columnsFieldIndex}
            onColumnsTypeIndexChange={this.handleColumnsTypeIndexChange}
            onColumnsFieldIndexChange={this.handleColumnsFieldIndexChange}
          />
          {formatManagement.opened && (
            <FormatManagement
              mode={formatManagement.mode}
              close={this.closeFormatManagement}
              onFolderCreate={this.handleFolderCreate}
              onSubmit={this.handleFormatManagementSubmit}
            />
          )}
        </ModalBody>
        <ModalFooter className="PedigreeImport__footer">
          <Flex>
            <Button {...getTestAttributes(MODULE_NAME, 'load-format', 'button')} disabled={submitting} onClick={this.handleLoadFormatClick}>
              {I18n.t('Load Format')}
            </Button>
            <Button {...getTestAttributes(MODULE_NAME, 'save-format', 'button')} disabled={submitting} onClick={this.handleSaveFormatClick}>
              {I18n.t('Save Format')}
            </Button>
          </Flex>

          <Flex>
            <Button {...getTestAttributes(MODULE_NAME, 'cancel', 'button')} disabled={submitting} onClick={close}>
              {I18n.t('Cancel')}
            </Button>
            <Button
              {...getTestAttributes(MODULE_NAME, 'submit', 'button')}
              bsStyle="primary"
              disabled={submitting}
              onClick={this.handleImport}
            >
              {I18n.t(submitting ? 'Loading' : 'Import')}
            </Button>
          </Flex>
        </ModalFooter>
      </Modal>
    );
  }
}